import { withSentry } from '@sentry/remix';
import * as React from 'react';
import { Outlet } from '@remix-run/react';
import { withEmotionCache } from '@emotion/react';
import { unstable_useEnhancedEffect as useEnhancedEffect } from '@mui/material';
import { LicenseInfo } from '@mui/x-license-pro';
import { json } from '@remix-run/node';
import cspNonce from '~/services/csp.server';
import { Links, Meta, Scripts, ScrollRestoration, useLoaderData } from '@remix-run/react';

import { useLDIdentify } from '~/thirdPartyConfigs/LaunchDarklyProvider';
import { getLDContextsFromRequest } from '~/thirdPartyConfigs/LaunchDarklyProvider.server';

import ErrorBoundaryComponent from '~/components/ErrorBoundary/ErrorBoundary';

import ClientStyleContext from './styles/ClientStyleContext';
import theme from './theme';

interface DocumentProps {
  children: React.ReactNode;
  title?: string;
  cspScriptNonce: string;
  GLOBAL_ENV: Record<string, string>;
}

export async function loader({ request }: { request: Request }) {
  const ldContexts = await getLDContextsFromRequest(request);
  return json({
    ENV: {
      MUIX_LICENSE_KEY: process.env.MUIX_LICENSE_KEY,
    },
    GLOBAL_ENV: {
      LAUNCHDARKLY_CLIENTSIDEID: process.env.LAUNCHDARKLY_CLIENTSIDEID,
      DATADOG_CLIENT_TOKEN: process.env.DATADOG_CLIENT_TOKEN,
      TPRIME_ENV: process.env.TPRIME_ENV,
      DATADOG_PROXY_URL: process.env.DATADOG_PROXY_URL,
    },
    ldContexts,
    cspScriptNonce: cspNonce,
  });
}

const Document = withEmotionCache(
  ({ children, title, cspScriptNonce, GLOBAL_ENV }: DocumentProps, emotionCache) => {
    const clientStyleData = React.useContext(ClientStyleContext);
    const reinjectStylesRef = React.useRef(true);

    // Only executed on client
    useEnhancedEffect(() => {
      if (reinjectStylesRef.current) {
        // re-link sheet container
        emotionCache.sheet.container = document.head; // eslint-disable-line no-param-reassign
        // re-inject tags
        const { tags } = emotionCache.sheet;
        emotionCache.sheet.flush();
        tags.forEach((tag) => {
          // eslint-disable-next-line no-underscore-dangle, @typescript-eslint/no-explicit-any
          (emotionCache.sheet as any)._insertTag(tag);
        });
        // reset cache to reapply global styles
        clientStyleData.reset();
        // ensure we only do this once per mount
        reinjectStylesRef.current = false;
      }
    }, [clientStyleData, emotionCache.sheet]);

    return (
      <html lang="en">
        <head>
          <meta charSet="utf-8" />
          <meta name="viewport" content="width=device-width,initial-scale=1" />
          <meta name="theme-color" content={theme.palette.primary.main} />
          {title ? <title>{title}</title> : null}
          <Meta />
          <Links />
          <meta name="emotion-insertion-point" content="emotion-insertion-point" />
        </head>
        <body>
          {children}
          <script
            dangerouslySetInnerHTML={{
              __html: `window.ENV = ${JSON.stringify(GLOBAL_ENV)}`,
            }}
            nonce={cspScriptNonce}
          />
          <ScrollRestoration nonce={cspScriptNonce} />
          <Scripts nonce={cspScriptNonce} />
        </body>
      </html>
    );
  },
);

// https://remix.run/api/conventions#default-export
// https://remix.run/api/conventions#route-filenames
function App() {
  const { ENV, ldContexts, cspScriptNonce, GLOBAL_ENV } = useLoaderData<typeof loader>();
  LicenseInfo.setLicenseKey(ENV.MUIX_LICENSE_KEY ?? '');
  useLDIdentify(ldContexts);
  const nonce = typeof document === 'undefined' ? cspScriptNonce : '';

  return (
    <Document cspScriptNonce={nonce} GLOBAL_ENV={GLOBAL_ENV}>
      <Outlet />
    </Document>
  );
}

export default withSentry(App);

// https://remix.run/docs/en/v1/api/conventions#errorboundary
export function ErrorBoundary() {
  return (
    <Document title="We've encountered an error">
      <ErrorBoundaryComponent />
    </Document>
  );
}
